import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import BarRestaurantTableDetailModal from './BarRestaurantTableDetailModal';
import './BarRestaurantOrderDetailModal.css';
import AppIcon from './icons/AppIcon';

export default function BarRestaurantOrderDetailModal({ card, order: orderProp, onClose, onStatusChange }) {
  const [order, setOrder] = useState(orderProp || null);
  const [loading, setLoading] = useState(!orderProp);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [updatingItems, setUpdatingItems] = useState(false);
  const [showTableModal, setShowTableModal] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);

  useEffect(() => {
    if (orderProp) {
      setOrder(orderProp);
      setLoading(false);
      return;
    }

    if (card?.entity_id) {
      loadOrderData();
    }
  }, [card?.entity_id, orderProp]);

  const loadOrderData = async () => {
    try {
      setLoading(true);
      console.log('[BarRestaurantOrderDetailModal] Carregando pedido com entity_id:', card.entity_id);
      
      const { data, error } = await supabase
        .from('bar_restaurant_orders')
        .select('*')
        .eq('id', card.entity_id)
        .single();

      if (error) {
        console.error('[BarRestaurantOrderDetailModal] Erro ao buscar pedido:', error);
        throw error;
      }
      
      console.log('[BarRestaurantOrderDetailModal] Pedido carregado:', data);
      setOrder(data);
    } catch (error) {
      console.error('[BarRestaurantOrderDetailModal] Erro ao carregar pedido:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'pedidos_em_aberto': 'Pedidos em aberto',
      'em_producao': 'Em Produção',
      'pronto': 'Pronto',
      'entregue': 'Entregue',
      'cancelado': 'Cancelado'
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      'pedidos_em_aberto': '#fff3cd',
      'em_producao': '#fff3cd',
      'pronto': '#d1ecf1',
      'entregue': '#d4edda',
      'cancelado': '#f8d7da'
    };
    return colorMap[status] || '#e2e3e5';
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('pt-BR');
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Agora';
    if (diffMins < 60) return `Há ${diffMins} min`;
    if (diffHours < 24) return `Há ${diffHours}h`;
    if (diffDays === 1) return 'Ontem';
    if (diffDays < 7) return `Há ${diffDays} dias`;
    return formatDate(timestamp);
  };

  if (loading) {
    return (
      <div className="bar-restaurant-modal-overlay" onClick={onClose}>
        <div className="bar-restaurant-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-loading">
            <AppIcon name="progress_activity" className="spinning" />
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="bar-restaurant-modal-overlay" onClick={onClose}>
        <div className="bar-restaurant-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-error">
            <p>Erro ao carregar dados do pedido</p>
            <button onClick={onClose}>Fechar</button>
          </div>
        </div>
      </div>
    );
  }

  const customFields = card?.custom_fields || order?.custom_fields || {};
  const orderPayload = order?.order_payload || {};
  const itensDetalhados = orderPayload?.itens || customFields.itens_detalhados || [];
  const statusAtual = order?.status_atual || customFields.status_atual || 'pedidos_em_aberto';
  const timestampEntrada = order?.timestamp_entrada_lista || customFields.timestamp_entrada_lista || order?.created_at;
  const historico = order?.historico_movimentacoes || [];
  const totalPedido = Number(orderPayload.total || customFields.total || 0);

  const statusOptions = [
    { value: 'pedidos_em_aberto', label: 'Pedidos em aberto' },
    { value: 'em_producao', label: 'Em Produção' },
    { value: 'pronto', label: 'Pronto' },
    { value: 'entregue', label: 'Entregue' },
    { value: 'cancelado', label: 'Cancelado' }
  ];

  const handleUpdateItems = async (updatedItems) => {
    if (!order?.id) return;

    try {
      setUpdatingItems(true);
      const response = await fetch(`/api/bar-restaurant/order/${order.id}/items`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itens: updatedItems })
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Erro ao atualizar itens');
      }

      setOrder(prev => ({
        ...prev,
        order_payload: {
          ...(prev?.order_payload || {}),
          itens: updatedItems,
          total: data.total
        }
      }));
    } catch (error) {
      console.error('[BarRestaurantOrderDetailModal] Erro ao atualizar itens:', error);
      alert('Erro ao atualizar itens: ' + error.message);
    } finally {
      setUpdatingItems(false);
    }
  };

  const handleChangeItemQty = (itemIndex, delta) => {
    const current = itensDetalhados[itemIndex];
    if (!current) return;
    const newQty = Math.max(0, Number(current.quantidade || 0) + delta);
    const updatedItems = itensDetalhados
      .map((item, idx) => (idx === itemIndex ? { ...item, quantidade: newQty } : item))
      .filter(item => Number(item.quantidade || 0) > 0);

    handleUpdateItems(updatedItems);
  };

  const handleRemoveItem = (itemIndex) => {
    const updatedItems = itensDetalhados.filter((_, idx) => idx !== itemIndex);
    handleUpdateItems(updatedItems);
  };

  const handleOpenTable = async () => {
    if (!order?.id) return;
    try {
      const response = await fetch(`/api/bar-restaurant/order/${order.id}/table`);
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Erro ao buscar mesa');
      }

      setSelectedTable(data.table);
      setShowTableModal(true);
    } catch (error) {
      console.error('[BarRestaurantOrderDetailModal] Erro ao abrir mesa:', error);
      alert('Erro ao abrir mesa: ' + error.message);
    }
  };

  const handleCloseTableModal = () => {
    setShowTableModal(false);
    setSelectedTable(null);
  };

  return (
    <div className="bar-restaurant-modal-overlay" onClick={onClose}>
      <div className="bar-restaurant-modal order-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="header-title">
            <h2>Pedido #{order.id?.slice(0, 8).toUpperCase()}</h2>
            <span className="header-subtitle">Mesa {orderPayload.mesa || customFields.mesa || '?'}</span>
          </div>
          <button className="btn-close" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        {/* Status Atual */}
        <div className="modal-status-section">
          <div className="status-display">
            <div className="status-info">
              <span className="status-label">Status Atual:</span>
              <span className="status-value">{getStatusLabel(statusAtual)}</span>
            </div>
            <div className="status-time">
              <AppIcon name="schedule" />
              <div className="time-info">
                <div>{formatTime(timestampEntrada)}</div>
                <div className="time-ago">{getTimeAgo(timestampEntrada)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Itens */}
        <div className="modal-items-section">
          <h3>Itens do Pedido</h3>
          <div className="items-list">
            {itensDetalhados.map((item, idx) => (
              <div key={idx} className="item-row">
                <div className="item-details">
                  <span className="item-qty">x{item.quantidade}</span>
                  <span className="item-name">{item.nome}</span>
                </div>
                <div className="item-price">R$ {item.preco_total?.toFixed(2) || '0.00'}</div>
                <div className="item-actions">
                  <button
                    type="button"
                    className="item-action-btn"
                    onClick={() => handleChangeItemQty(idx, -1)}
                    disabled={updatingItems}
                  >
                    -
                  </button>
                  <button
                    type="button"
                    className="item-action-btn"
                    onClick={() => handleChangeItemQty(idx, 1)}
                    disabled={updatingItems}
                  >
                    +
                  </button>
                  <button
                    type="button"
                    className="item-action-btn danger"
                    onClick={() => handleRemoveItem(idx)}
                    disabled={updatingItems}
                  >
                    <AppIcon name="delete" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="items-total">
            <span>Total:</span>
            <span className="total-value">R$ {totalPedido.toFixed(2)}</span>
          </div>
        </div>

        {/* Histórico de Movimentações */}
        {historico.length > 0 && (
          <div className="modal-history-section">
            <h3>Histórico de Movimentações</h3>
            <div className="history-timeline">
              {historico.map((item, idx) => (
                <div key={idx} className="history-item">
                  <div className="history-marker">
                    <AppIcon name={item.acao === 'confirmado' ? 'check_circle' : 'arrow_forward'} />
                  </div>
                  <div className="history-content">
                    <div className="history-action">
                      {item.acao === 'confirmado' ? '✓ Confirmado' : `→ ${item.lista}`}
                    </div>
                    <div className="history-time">
                      {formatTime(item.timestamp)}
                      <span className="history-date">{formatDate(item.timestamp)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer com Botões */}
        <div className="modal-footer">
          <div className="status-selector">
            <button
              className="btn-status"
              onClick={() => setShowStatusMenu(!showStatusMenu)}
            >
              <AppIcon name="edit" />
              Alterar Status
            </button>
            {showStatusMenu && (
              <div className="status-menu">
                {statusOptions.map((option) => (
                  <button
                    key={option.value}
                    className={`status-option ${statusAtual === option.value ? 'active' : ''}`}
                    onClick={() => {
                      if (onStatusChange) {
                        onStatusChange(option.value);
                      }
                      setShowStatusMenu(false);
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button className="btn-view-table" onClick={handleOpenTable}>
            <AppIcon name="table_restaurant" />
            Ver mesa
          </button>
          <button className="btn-close-modal" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>

      {showTableModal && selectedTable && (
        <BarRestaurantTableDetailModal table={selectedTable} onClose={handleCloseTableModal} />
      )}
    </div>
  );
}
